// Tracking params arrive from ad URLs, and ad URLs lie. Meta only substitutes
// {{campaign.name}}-style URL macros on a click-through to a website; a
// Click-to-WhatsApp ad opens WhatsApp directly and a copied/previewed URL is
// never substituted at all, so the literal string "{{campaign.name}}" - or its
// URL-encoded form "%7B%7Bcampaign.name%7D%7D" - arrives as the param value.
// Every place that parses tracking params runs values through
// sanitizeTrackingParam so those literals die at the boundary, before they are
// persisted (localStorage, Monday, Sheets) or interpolated into a notification.

export const TRACKING_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'device',
  'fbclid',
  'gclid',
  'landing_referrer',
  'ctwa_clid',
  'ctwa_campaign',
  'ctwa_ad',
  'ctwa_source',
] as const;

/**
 * Returns the trimmed value, or null when the value is empty or is an
 * unsubstituted URL macro - Meta's double-brace form ("{{campaign.name}}") or
 * the single-brace form used by ChatGPT Ads ("{campaign_id}", "{ad_id}"),
 * raw or URL-encoded. The single-brace pattern is deliberately narrow
 * (word characters and dots only) so a real value that happens to contain
 * braces is left alone.
 */
export function sanitizeTrackingParam(value: string | null | undefined): string | null {
  if (value == null) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/^\{\{.*\}\}$/.test(trimmed)) return null;
  if (/^%7B%7B.*%7D%7D$/i.test(trimmed)) return null;
  if (/^\{[A-Za-z0-9_.]+\}$/.test(trimmed)) return null;
  if (/^%7B[A-Za-z0-9_.]+%7D$/i.test(trimmed)) return null;
  return trimmed;
}

/**
 * Sanitize the tracking keys of a params object in one pass. Clean values are
 * kept (trimmed); macro/empty values are removed entirely, so downstream
 * `||`-fallbacks ("unknown" / "Direct") take over instead of a raw macro
 * passing through. Non-tracking keys are untouched.
 */
export function sanitizeTrackingParams<T extends object>(params: T): T {
  const out = { ...params } as Record<string, unknown>;
  for (const key of TRACKING_PARAM_KEYS) {
    if (key in out) {
      const clean = sanitizeTrackingParam(out[key] as string | null | undefined);
      if (clean === null) delete out[key];
      else out[key] = clean;
    }
  }
  return out as T;
}
